import { useParams, Link, Navigate } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { comparisons, getComparisonBySlug } from '@/content/comparisons';
import { NotifyForm } from '@/components/NotifyForm';
import { useSeo } from '@/seo/useSeo';

export function CompareDetail(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const comparison = getComparisonBySlug(slug);

  useSeo({
    title: comparison ? `Nexus vs. ${comparison.vendor}` : 'Comparison not found',
    description: comparison
      ? `${comparison.summary} Vendor-neutral questions worth asking, and what Nexus does differently.`
      : 'This comparison page could not be found.',
    path: `/compare/${slug ?? ''}`,
    schema: comparison
      ? {
          '@type': 'FAQPage',
          mainEntity: comparison.whatToAskThem.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.why },
          })),
        }
      : undefined,
  });

  if (!comparison) return <Navigate to="/compare" replace />;

  const Icon = comparison.icon;
  const related = comparisons.filter((c) => c.slug !== comparison.slug).slice(0, 3);

  return (
    <div>
      <section className="relative overflow-hidden pt-16 pb-16 md:pt-24 md:pb-24">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-nexus-grid bg-grid [mask-image:radial-gradient(circle_at_50%_0%,#000,transparent_70%)]"
        />
        <div className="container-content relative max-w-[760px]">
          <p className="text-xs text-white/40">
            <Link to="/compare" className="text-white/40 no-underline hover:text-nexus-purple-light">
              Compare
            </Link>{' '}
            / {comparison.vendor}
          </p>
          <div className="mb-5 mt-6 flex h-12 w-12 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
            <Icon fontSize="medium" />
          </div>
          <div className="mb-4">
            <Chip
              label={comparison.category}
              size="small"
              className="h-[22px] border border-nexus-purple/35 bg-transparent text-[10.5px] font-bold tracking-wide text-nexus-purple-light"
            />
          </div>
          <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-nexus-ink md:text-[44px]">
            Nexus vs. {comparison.vendor}
          </h1>
          <p className="mt-5 text-lg text-white/70">{comparison.summary}</p>
          <p className="mt-4 text-sm text-white/50">{comparison.pricingShape}</p>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content max-w-[820px]">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
            Questions worth asking {comparison.vendor} directly
          </h2>
          <p className="mt-3 text-sm text-white/60">
            We don&apos;t have verified inside knowledge of {comparison.vendor}&apos;s architecture, so
            these are questions to put to them, not claims about the answer.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            {comparison.whatToAskThem.map((item) => (
              <div
                key={item.q}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4"
              >
                <HelpOutlineIcon fontSize="small" className="mt-0.5 flex-shrink-0 text-nexus-purple-light" />
                <div>
                  <p className="text-[15px] font-semibold text-nexus-ink">{item.q}</p>
                  <p className="mt-1 text-sm text-white/60">{item.why}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-white/[0.015] py-16 md:py-24">
        <div className="container-content max-w-[820px]">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
            What Nexus does differently
          </h2>
          <p className="mt-3 text-sm text-white/60">
            Established facts about our own architecture — see the{' '}
            <Link to="/security" className="text-nexus-purple-light no-underline hover:underline">
              trust center
            </Link>{' '}
            for the detail behind each one.
          </p>
          <ul className="mt-6 flex flex-col gap-3">
            {comparison.whatNexusDoesDifferently.map((d) => (
              <li key={d} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <CheckCircleOutlineIcon fontSize="small" className="mt-0.5 flex-shrink-0 text-nexus-purple-light" />
                <span className="text-sm leading-relaxed text-white/75">{d}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-2xl border border-nexus-purple/25 bg-nexus-purple/[0.06] p-5">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-nexus-purple-light">
              Honest about where this is
            </p>
            <p className="mt-2 text-sm leading-relaxed text-white/75">
              {comparison.vendor} is a real, established platform with a live customer base today.
              Nexus is earlier — live in our own MSP practice, open to a limited design-partner
              cohort, without public pricing yet. If you need a platform that already has GA
              polish and a large existing user base, that&apos;s a real factor in {comparison.vendor}
              &apos;s favor right now, and we&apos;d rather say so than pretend otherwise.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">Other comparisons</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {related.map((c) => {
              const RelIcon = c.icon;
              return (
                <Link
                  key={c.slug}
                  to={`/compare/${c.slug}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                    <RelIcon fontSize="small" />
                  </div>
                  <h3 className="font-display text-[15px] font-semibold text-nexus-ink">
                    Nexus vs. {c.vendor}
                  </h3>
                  <p className="mt-1.5 text-sm text-white/65">{c.summary}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
            <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              Ask us these questions directly.
            </h2>
            <p className="mx-auto mt-3 max-w-[520px] text-white/70">
              Join the design-partner cohort and put the same questions to the team actually
              building it.
            </p>
            <div className="mt-8 flex justify-center">
              <NotifyForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
